import type { CreateBriefData, SearchCandidatesData } from '@/hooks/use-briefs'
import type { SearchSessionSummary } from '@/hooks/use-search-sessions'

export type SearchCriteriaInput = Pick<
  SearchCandidatesData,
  'required_skills' | 'preferred_skills' | 'location' | 'instructions'
> & {
  experience_level?: SearchCandidatesData['experience_level'] | 'all' | string
}

export type BriefDraftPrefill = Partial<CreateBriefData> & {
  titleSuggestion?: string
}

const EXPERIENCE_LABELS: Record<string, string> = {
  junior: 'Junior',
  mid: 'Mid',
  senior: 'Senior',
  executive: 'Executive',
}

function formatExperienceLevel(level?: string | null): string | null {
  if (!level || level === 'all') return null
  return EXPERIENCE_LABELS[level] ?? level.charAt(0).toUpperCase() + level.slice(1)
}

function mergeInstructions(
  instructions?: string | null,
  experienceLevel?: string | null,
): string | undefined {
  const parts: string[] = []
  const trimmed = instructions?.trim()
  if (trimmed) parts.push(trimmed)
  const expLabel = formatExperienceLevel(experienceLevel)
  if (expLabel) parts.push(`Experience level: ${expLabel}`)
  return parts.length > 0 ? parts.join('\n\n') : undefined
}

export function suggestBriefTitleFromSearch(criteria: SearchCriteriaInput): string {
  const required = criteria.required_skills ?? []
  if (required.length > 0) {
    return `${required.slice(0, 3).join(', ')} search`
  }
  const preferred = criteria.preferred_skills ?? []
  if (preferred.length > 0) {
    return `${preferred.slice(0, 3).join(', ')} search`
  }
  const location = criteria.location?.trim()
  if (location) return `Search — ${location}`
  return 'Brief from analysis'
}

export function hasSearchCriteria(criteria: SearchCriteriaInput): boolean {
  const hasSkills =
    (criteria.required_skills?.length ?? 0) > 0 ||
    (criteria.preferred_skills?.length ?? 0) > 0
  const hasLocation = Boolean(criteria.location?.trim())
  const hasInstructions = Boolean(criteria.instructions?.trim())
  const hasExperience =
    Boolean(criteria.experience_level) && criteria.experience_level !== 'all'
  return hasSkills || hasLocation || hasInstructions || hasExperience
}

export function searchCriteriaToBriefDraft(criteria: SearchCriteriaInput): BriefDraftPrefill {
  const titleSuggestion = suggestBriefTitleFromSearch(criteria)
  return {
    title: titleSuggestion,
    titleSuggestion,
    location: criteria.location?.trim() || undefined,
    instructions: mergeInstructions(criteria.instructions, criteria.experience_level),
    required_skills:
      criteria.required_skills && criteria.required_skills.length > 0
        ? criteria.required_skills
        : undefined,
    preferred_skills:
      criteria.preferred_skills && criteria.preferred_skills.length > 0
        ? criteria.preferred_skills
        : undefined,
    status: 'draft',
  }
}

export function searchSessionToBriefDraft(
  session: Pick<
    SearchSessionSummary,
    'required_skills' | 'preferred_skills' | 'location' | 'instructions' | 'experience_level'
  >,
): BriefDraftPrefill {
  return searchCriteriaToBriefDraft({
    required_skills: session.required_skills,
    preferred_skills: session.preferred_skills,
    location: session.location ?? undefined,
    instructions: session.instructions,
    experience_level: session.experience_level as SearchCriteriaInput['experience_level'],
  })
}
